/**
 * AuthScreen — magic link sign-in. Per app.pact.constraint.auth.002.
 * Surface: auth.signin
 * Dev mode: shows a "Skip — view as Dev" button so the operator can enter the app
 * without setting up Supabase.
 */
import { useState } from 'react'
import { signInWithMagicLink } from '../auth'
import { useApp } from '../store'
import { Sparkles, Beaker } from 'lucide-react'

export function AuthScreen() {
  const { setDev } = useApp()
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    const { error } = await signInWithMagicLink(email)
    setBusy(false)
    if (error) {
      setError(error.message)
    } else {
      setSent(true)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-skin-bg">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-garden-100 text-garden-800 mb-4">
            <Sparkles className="w-8 h-8" />
          </div>
          <h1 className="text-3xl font-semibold text-skin-text">Home-studio</h1>
          <p className="text-slate-500 mt-2">A quiet place for our family to work and grow together.</p>
        </div>

        {sent ? (
          <div className="bg-garden-50 border border-garden-200 rounded-2xl p-6 text-center">
            <h2 className="text-lg font-medium text-garden-800">Check your email</h2>
            <p className="text-garden-700 mt-2">We sent a magic link to <span className="font-medium">{email}</span>.</p>
            <p className="text-sm text-slate-500 mt-4">Click the link in the email to sign in.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
                Family email
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@family.com"
                className="w-full px-3 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-garden-500 min-h-touch"
              />
            </div>
            {error && (
              <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={busy || !email}
              className="w-full bg-garden-800 text-white py-2.5 rounded-lg font-medium hover:bg-garden-900 disabled:opacity-50 disabled:cursor-not-allowed min-h-touch"
            >
              {busy ? 'Sending…' : 'Send magic link'}
            </button>
            <p className="text-xs text-slate-500 text-center">
              New members are added to the family by an existing member. If you don't have access yet, ask a family adult to add your email.
            </p>
          </form>
        )}

        <div className="mt-4 text-center">
          <button
            onClick={() => setDev(true)}
            className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 px-3 py-2 rounded-lg hover:bg-slate-100"
          >
            <Beaker className="w-3 h-3" />
            Skip — view as Dev (local seed data, no Supabase)
          </button>
        </div>
      </div>
    </div>
  )
}
